import { type JSX, useEffect, useId, useState } from 'react';

import { ApiFehler } from '../api/fehler';
import {
  BEZUGSNAME,
  FOLGEAKTIONEN,
  FOLGEAKTIONSNAME,
  folgeaktionAnlegen,
  folgeaktionErgebnis,
  wiedervorlageAnlegen,
  zuordnung as ladeZuordnung,
  type Bezugszeile,
  type Folgeaktionsart,
  type Folgeaktionsziel,
  type Folgeaktionszeile,
  type Vorgangszeile,
} from '../api/support';
import { alsZeitpunkt, heute } from '../datum';
import { Abschlussreihe } from '../komponenten/Abschlussreihe';
import { Dialog } from '../komponenten/Dialog';
import { Meldung } from '../komponenten/Meldung';
import { markenklasse } from '../marken';
import { fange } from './bearbeitungshilfe';

/**
 * FOLGEAKTIONEN AUS DEM VORGANG (SUP-23; Auftrag CRM-178).
 *
 * Erst wird die Folgeaktion am Vorgang angelegt, dann geht die vorhandene
 * Maske auf — vorbelegt, soweit sie es kann. Das Angebot meldet nach dem
 * Speichern selbst zurück, was entstand; für Buchung und Rechnung ordnet man
 * das Ergebnis aus den Bezügen des Vorgangs zu. Die Wiedervorlage entsteht
 * gleich hier.
 */

/** Was die Angebotsmaske braucht, um nach dem Speichern zum Vorgang zurückzukehren. */
export type Angebotsfolge = {
  readonly kunde: string;
  readonly vorgang: string;
  readonly postfach: string;
  readonly nummer: string;
  readonly folgeaktion: string;
};

/**
 * Die Wege in die anderen Masken. Fehlt einer, fehlt das Recht — die
 * Folgeaktion wird dann nicht angeboten.
 */
export type Folgewege = {
  readonly angebot?: ((folge: Angebotsfolge) => void) | undefined;
  readonly angebotOeffnen?: ((id: string) => void) | undefined;
  readonly buchung?: ((nummer: string, reiter: 'uebersicht' | 'teilnehmer' | 'storno') => void) | undefined;
  readonly rechnung?: ((id: string) => void) | undefined;
};

/** Welche Belegart das Ergebnis einer Folgeaktion ist. */
export const ZIELART: Readonly<Record<Folgeaktionsart, Folgeaktionsziel>> = {
  angebot: 'angebot',
  buchung_aendern: 'buchung',
  buchung_stornieren: 'buchung',
  teilnehmer_aendern: 'buchung',
  rechnung_senden: 'rechnung',
  wiedervorlage: 'wiedervorlage',
};

const REITER: Readonly<Partial<Record<Folgeaktionsart, 'uebersicht' | 'teilnehmer' | 'storno'>>> = {
  buchung_aendern: 'uebersicht',
  buchung_stornieren: 'storno',
  teilnehmer_aendern: 'teilnehmer',
};

/** Ob es für die Folgeaktion einen Weg gibt (das Recht auf die Zielmaske). */
function hatWeg(art: Folgeaktionsart, wege: Folgewege): boolean {
  switch (art) {
    case 'angebot':
      return wege.angebot !== undefined;
    case 'buchung_aendern':
    case 'buchung_stornieren':
    case 'teilnehmer_aendern':
      return wege.buchung !== undefined;
    case 'rechnung_senden':
      return wege.rechnung !== undefined;
    case 'wiedervorlage':
      return true;
  }
}

/** Warum eine Folgeaktion gesperrt ist — oder null. */
function sperrgrund(art: Folgeaktionsart, kunde: string | null, buchung: Bezugszeile | undefined, rechnung: Bezugszeile | undefined): string | null {
  if (art === 'wiedervorlage') return null;
  if (kunde === null) return 'Der Vorgang hat noch keinen Kunden — erst zuordnen.';
  if (ZIELART[art] === 'buchung' && buchung === undefined) return 'Keine Buchung als Bezug verknüpft.';
  if (art === 'rechnung_senden' && rechnung === undefined) return 'Keine Rechnung als Bezug verknüpft.';
  return null;
}

/** Ob die Folgeaktion angeboten werden kann — gebraucht auch von den KI-Schritten (SUP-14). */
export function folgeaktionMoeglich(
  art: Folgeaktionsart,
  wege: Folgewege,
  kunde: string | null,
  buchung: Bezugszeile | undefined,
  rechnung: Bezugszeile | undefined,
): string | null {
  if (!hatWeg(art, wege)) return 'Dafür fehlt das Recht auf die Maske.';
  return sperrgrund(art, kunde, buchung, rechnung);
}

/** SUP-23: erst festhalten, dann die Maske öffnen (nicht für die Wiedervorlage — die hat ihren Dialog). */
export async function folgeaktionOeffnen(
  v: Vorgangszeile,
  art: Exclude<Folgeaktionsart, 'wiedervorlage'>,
  ziel: { readonly kunde: string | null; readonly buchung: Bezugszeile | undefined; readonly rechnung: Bezugszeile | undefined },
  wege: Folgewege,
): Promise<void> {
  const fa = await folgeaktionAnlegen(v.id, art);
  if (art === 'angebot' && ziel.kunde !== null) {
    wege.angebot?.({ kunde: ziel.kunde, vorgang: v.id, postfach: v.postfach, nummer: v.nummer, folgeaktion: fa.id });
  } else if (art === 'rechnung_senden' && ziel.rechnung !== undefined) {
    wege.rechnung?.(ziel.rechnung.ziel);
  } else if (ziel.buchung !== undefined) {
    wege.buchung?.(ziel.buchung.nummer, REITER[art] ?? 'uebersicht');
  }
}

/** SUP-23: die Auswahl der Folgeaktionen; die Wiedervorlage geht in ihren eigenen Dialog. */
export function Folgeaktionswahl({
  vorgang: v,
  wege,
  beiWiedervorlage,
  beiSchliessen,
}: {
  readonly vorgang: Vorgangszeile;
  readonly wege: Folgewege;
  readonly beiWiedervorlage: () => void;
  readonly beiSchliessen: () => void;
}): JSX.Element {
  const kennung = useId();
  const [bezuege, setzeBezuege] = useState<readonly Bezugszeile[] | null>(null);
  const [kunde, setzeKunde] = useState<string | null>(v.kunde);
  const [buchungWahl, setzeBuchungWahl] = useState('');
  const [rechnungWahl, setzeRechnungWahl] = useState('');
  const [fehler, setzeFehler] = useState<ApiFehler | null>(null);
  const [laeuft, setzeLaeuft] = useState(false);

  // Kunde und Bezüge frisch: Die Zuordnung kann sich seit dem Laden des Vorgangs geändert haben.
  useEffect(() => {
    let gilt = true;
    ladeZuordnung(v.id).then(
      (z) => {
        if (!gilt) return;
        setzeBezuege(z.bezuege);
        setzeKunde(z.kunde?.id ?? null);
      },
      (f: unknown) => {
        if (!gilt) return;
        setzeBezuege([]);
        fange(setzeFehler, f);
      },
    );
    return () => {
      gilt = false;
    };
  }, [v.id]);

  const buchungen = (bezuege ?? []).filter((b) => b.art === 'buchung');
  const rechnungen = (bezuege ?? []).filter((b) => b.art === 'rechnung');
  const buchung = buchungen.find((b) => b.id === buchungWahl) ?? buchungen[0];
  const rechnung = rechnungen.find((b) => b.id === rechnungWahl) ?? rechnungen[0];

  const starten = async (art: Folgeaktionsart): Promise<void> => {
    if (art === 'wiedervorlage') {
      beiWiedervorlage();
      return;
    }
    setzeLaeuft(true);
    setzeFehler(null);
    try {
      await folgeaktionOeffnen(v, art, { kunde, buchung, rechnung }, wege);
    } catch (f) {
      fange(setzeFehler, f);
    } finally {
      setzeLaeuft(false);
    }
  };

  const angeboten = FOLGEAKTIONEN.filter((a) => hatWeg(a, wege));

  return (
    <Dialog titel="Folgeaktion" unterzeile={`Aus dem Vorgang ${v.nummer}`} breite="mittel" beiSchliessen={beiSchliessen}>
      <>
        {fehler !== null && <Meldung fehler={fehler} />}
        {bezuege === null && <p role="status">Wird geladen …</p>}
        {buchungen.length > 1 && (
          <div className="feld">
            <label className="feld__beschriftung" htmlFor={`${kennung}-buchung`}>Buchung</label>
            <select
              id={`${kennung}-buchung`}
              className="feld__eingabe"
              value={buchung?.id ?? ''}
              onChange={(e) => setzeBuchungWahl(e.target.value)}
            >
              {buchungen.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.nummer}
                </option>
              ))}
            </select>
          </div>
        )}
        {rechnungen.length > 1 && (
          <div className="feld">
            <label className="feld__beschriftung" htmlFor={`${kennung}-rechnung`}>Rechnung</label>
            <select
              id={`${kennung}-rechnung`}
              className="feld__eingabe"
              value={rechnung?.id ?? ''}
              onChange={(e) => setzeRechnungWahl(e.target.value)}
            >
              {rechnungen.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.nummer}
                </option>
              ))}
            </select>
          </div>
        )}
        {bezuege !== null && (
          <ul className="zuordnung__bezuege" aria-label="Folgeaktionen">
            {angeboten.map((art) => {
              const grund = sperrgrund(art, kunde, buchung, rechnung);
              const ziel = ZIELART[art] === 'buchung' ? buchung : art === 'rechnung_senden' ? rechnung : undefined;
              return (
                <li key={art}>
                  <button
                    type="button"
                    className={art === 'buchung_stornieren' ? 'knopf knopf--eingriff' : 'knopf'}
                    disabled={laeuft || grund !== null}
                    onClick={() => void starten(art)}
                  >
                    {FOLGEAKTIONSNAME[art]}
                  </button>
                  {grund === null && ziel !== undefined && <span className="hilfe"> {ziel.nummer}</span>}
                  {grund !== null && (
                    <>
                      <br />
                      <span className="hilfe">{grund}</span>
                    </>
                  )}
                </li>
              );
            })}
          </ul>
        )}
        <p className="feld__hilfe">
          Die Folgeaktion wird am Vorgang festgehalten, dann öffnet sich die Maske. Ein Angebot meldet sich nach dem
          Speichern selbst zurück; eine geänderte Buchung oder Rechnung ordnet man danach unter „Ergebnis zuordnen" zu.
        </p>
        <Abschlussreihe
          abbruch={
            <button type="button" className="knopf" onClick={beiSchliessen}>
              Abbrechen
            </button>
          }
        >
          {null}
        </Abschlussreihe>
      </>
    </Dialog>
  );
}

/** SUP-23, KUN-15: eine Wiedervorlage aus dem Vorgang — Betreff, Fälligkeit, Beschreibung. */
export function Wiedervorlagedialog({
  vorgang: v,
  vorbelegung,
  beiAngelegt,
  beiSchliessen,
}: {
  readonly vorgang: Vorgangszeile;
  /** SUP-14: aus einem KI-Schritt — Fälligkeit und Beschreibung vorbelegt, prüfen und anlegen tut der Anwender. */
  readonly vorbelegung?: { readonly faelligAm: string | null; readonly beschreibung: string } | undefined;
  readonly beiAngelegt: () => Promise<void> | void;
  readonly beiSchliessen: () => void;
}): JSX.Element {
  const kennung = useId();
  const [betreff, setzeBetreff] = useState(`Vorgang ${v.nummer}: ${v.betreff}`);
  const [faelligAm, setzeFaelligAm] = useState(vorbelegung?.faelligAm ?? heute());
  const [beschreibung, setzeBeschreibung] = useState(vorbelegung?.beschreibung ?? '');
  const [fehler, setzeFehler] = useState<ApiFehler | null>(null);
  const [laeuft, setzeLaeuft] = useState(false);

  const anlegen = async (): Promise<void> => {
    setzeLaeuft(true);
    setzeFehler(null);
    try {
      await wiedervorlageAnlegen(v.id, {
        betreff: betreff.trim(),
        faelligAm,
        ...(beschreibung.trim() === '' ? {} : { beschreibung: beschreibung.trim() }),
      });
      await beiAngelegt();
    } catch (f) {
      fange(setzeFehler, f);
    } finally {
      setzeLaeuft(false);
    }
  };

  return (
    <Dialog titel="Wiedervorlage anlegen" unterzeile={`Aus dem Vorgang ${v.nummer}`} breite="mittel" beiSchliessen={beiSchliessen}>
      <>
        {fehler !== null && <Meldung fehler={fehler} />}
        <div className="feld">
          <label className="feld__beschriftung" htmlFor={`${kennung}-betreff`}>
            Betreff <span className="feld__pflicht">Pflicht</span>
          </label>
          <input id={`${kennung}-betreff`} className="feld__eingabe" value={betreff} onChange={(e) => setzeBetreff(e.target.value)} />
        </div>
        <div className="feld">
          <label className="feld__beschriftung" htmlFor={`${kennung}-faellig`}>
            Fällig am <span className="feld__pflicht">Pflicht</span>
          </label>
          <input
            id={`${kennung}-faellig`}
            className="feld__eingabe"
            type="date"
            value={faelligAm}
            onChange={(e) => setzeFaelligAm(e.target.value)}
          />
        </div>
        <div className="feld">
          <label className="feld__beschriftung" htmlFor={`${kennung}-beschreibung`}>Beschreibung</label>
          <textarea
            id={`${kennung}-beschreibung`}
            className="feld__eingabe"
            rows={4}
            value={beschreibung}
            onChange={(e) => setzeBeschreibung(e.target.value)}
          />
        </div>
        <Abschlussreihe
          abbruch={
            <button type="button" className="knopf" disabled={laeuft} onClick={beiSchliessen}>
              Abbrechen
            </button>
          }
        >
          <button
            type="button"
            className="knopf knopf--haupt"
            disabled={laeuft || betreff.trim() === '' || faelligAm === ''}
            onClick={() => void anlegen()}
          >
            Anlegen
          </button>
        </Abschlussreihe>
      </>
    </Dialog>
  );
}

/** SUP-23: die Folgeaktionen eines Vorgangs mit ihrem Ergebnis. */
export function Folgeaktionsliste({
  vorgang,
  folgeaktionen,
  wege,
  bearbeitbar,
  beiAenderung,
}: {
  readonly vorgang: string;
  readonly folgeaktionen: readonly Folgeaktionszeile[];
  readonly wege: Folgewege;
  readonly bearbeitbar: boolean;
  readonly beiAenderung: () => Promise<void>;
}): JSX.Element {
  const [zuordnen, setzeZuordnen] = useState<Folgeaktionszeile | null>(null);

  if (folgeaktionen.length === 0) return <p className="hilfe">Noch keine Folgeaktion.</p>;

  const oeffnen = (fa: Folgeaktionszeile): (() => void) | null => {
    if (fa.ziel === null) return null;
    const ziel = fa.ziel;
    if (fa.zielArt === 'angebot' && wege.angebotOeffnen !== undefined) return () => wege.angebotOeffnen?.(ziel);
    if (fa.zielArt === 'rechnung' && wege.rechnung !== undefined) return () => wege.rechnung?.(ziel);
    if (fa.zielArt === 'buchung' && fa.nummer !== null && wege.buchung !== undefined) {
      const nummer = fa.nummer;
      return () => wege.buchung?.(nummer, 'uebersicht');
    }
    return null;
  };

  return (
    <>
      <ul className="vorgang__notizen" aria-label="Folgeaktionen des Vorgangs">
        {folgeaktionen.map((fa) => {
          const auf = oeffnen(fa);
          return (
            <li key={fa.id}>
              <strong>{FOLGEAKTIONSNAME[fa.art]}</strong>{' '}
              {fa.stand === 'erledigt' ? (
                <span className={markenklasse('abschluss')}>erledigt</span>
              ) : (
                <span className={markenklasse('laufend')}>angestoßen</span>
              )}
              {fa.nummer !== null && <> {fa.nummer}</>}
              <br />
              <span className="hilfe">
                {fa.von ?? 'System'} · {alsZeitpunkt(fa.am) ?? fa.am}
              </span>
              {(auf !== null || (bearbeitbar && fa.stand === 'angestossen' && fa.zielArt === null && fa.art !== 'wiedervorlage')) && (
                <div className="knopfreihe">
                  {bearbeitbar && fa.stand === 'angestossen' && fa.zielArt === null && fa.art !== 'wiedervorlage' && (
                    <button type="button" className="knopf knopf--klein" onClick={() => setzeZuordnen(fa)}>
                      Ergebnis zuordnen …
                    </button>
                  )}
                  {auf !== null && (
                    <button type="button" className="knopf knopf--klein" data-auch-telefon onClick={auf}>
                      Öffnen
                    </button>
                  )}
                </div>
              )}
            </li>
          );
        })}
      </ul>
      {zuordnen !== null && (
        <Ergebniswahl
          vorgang={vorgang}
          folgeaktion={zuordnen}
          beiZugeordnet={async () => {
            setzeZuordnen(null);
            await beiAenderung();
          }}
          beiSchliessen={() => setzeZuordnen(null)}
        />
      )}
    </>
  );
}

/** SUP-23: das Ergebnis einer Folgeaktion aus den Bezügen des Vorgangs wählen. */
function Ergebniswahl({
  vorgang,
  folgeaktion: fa,
  beiZugeordnet,
  beiSchliessen,
}: {
  readonly vorgang: string;
  readonly folgeaktion: Folgeaktionszeile;
  readonly beiZugeordnet: () => Promise<void>;
  readonly beiSchliessen: () => void;
}): JSX.Element {
  const zielArt = ZIELART[fa.art];
  const [bezuege, setzeBezuege] = useState<readonly Bezugszeile[] | null>(null);
  const [fehler, setzeFehler] = useState<ApiFehler | null>(null);
  const [laeuft, setzeLaeuft] = useState(false);

  useEffect(() => {
    let gilt = true;
    ladeZuordnung(vorgang).then(
      (z) => {
        if (gilt) setzeBezuege(z.bezuege.filter((b) => b.art === zielArt));
      },
      (f: unknown) => {
        if (!gilt) return;
        setzeBezuege([]);
        fange(setzeFehler, f);
      },
    );
    return () => {
      gilt = false;
    };
  }, [vorgang, zielArt]);

  const waehlen = async (b: Bezugszeile): Promise<void> => {
    setzeLaeuft(true);
    setzeFehler(null);
    try {
      await folgeaktionErgebnis(fa.id, zielArt, b.ziel);
      await beiZugeordnet();
    } catch (f) {
      fange(setzeFehler, f);
    } finally {
      setzeLaeuft(false);
    }
  };

  return (
    <Dialog titel="Ergebnis zuordnen" unterzeile={FOLGEAKTIONSNAME[fa.art]} breite="schmal" beiSchliessen={beiSchliessen}>
      <>
        {fehler !== null && <Meldung fehler={fehler} />}
        {bezuege === null && <p role="status">Wird geladen …</p>}
        {bezuege !== null && bezuege.length === 0 && (
          <p className="feld__hilfe">
            Am Vorgang ist {zielArt === 'rechnung' ? 'keine Rechnung' : zielArt === 'angebot' ? 'kein Angebot' : 'keine Buchung'}{' '}
            als Bezug verknüpft. Erst unter „Bezüge" die Nummer verknüpfen, dann hier zuordnen.
          </p>
        )}
        {bezuege !== null && bezuege.length > 0 && (
          <ul className="zuordnung__bezuege" aria-label="Bezüge">
            {bezuege.map((b) => (
              <li key={b.id}>
                {zielArt !== 'wiedervorlage' && <span className="hilfe">{BEZUGSNAME[zielArt]} </span>}
                {b.nummer}{' '}
                <button type="button" className="knopf knopf--klein" disabled={laeuft} onClick={() => void waehlen(b)}>
                  Zuordnen
                </button>
              </li>
            ))}
          </ul>
        )}
        <Abschlussreihe
          abbruch={
            <button type="button" className="knopf" disabled={laeuft} onClick={beiSchliessen}>
              Abbrechen
            </button>
          }
        >
          {null}
        </Abschlussreihe>
      </>
    </Dialog>
  );
}
